<template>
  <v-form ref="ProcedureReportFilterForm" class="fill-width">
    <ExpandableFilterCard
      class="procedure-report-filter"
      elevation="2"
      :auto-search-on-always-visible-change="false"
      :expand-button-hover-text-collapsed="$t('procedureReport.filter.expandSearch')"
      :expand-button-hover-text-expanded="$t('procedureReport.filter.collapseSearch')"
      @on-expand-change="$emit('expand-change', $event)"
      @on-expand-transition-end="$emit('expand-transition-end', $event)"
      @on-search="searchRecords"
      @on-clear="resetFilters">
      <template v-slot:always-visible>
        <div class="procedure-report-filter__fields">
          <PaginatedAutocompleteMulti
            :label="$t('procedureReport.fields.carrier')"
            :fetch="fetchCarriers"
            @input="setCarrier"
            :value="filter.carrierId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <Textfield
            :label="$t('procedureReport.fields.procedureCode')"
            @input="setProcedureCode"
            @focus="focusTrackValues.procedureCodeId = filter.procedureCodeId"
            @blur="searchFromMainTextFilter(filter.procedureCodeId, 'procedureCodeId')"
            @click:clear="clearMainTextFilter('procedureCodeId')"
            :value="filter.procedureCodeId"
            v-bind="textFieldAttrs"
          />

          <Textfield
            :label="$t('procedureReport.fields.procedureDescription')"
            @input="setProcedureDescription"
            @focus="focusTrackValues.procedureDescriptionId = filter.procedureDescriptionId"
            @blur="searchFromMainTextFilter(filter.procedureDescriptionId, 'procedureDescriptionId')"
            @click:clear="clearMainTextFilter('procedureDescriptionId')"
            :value="filter.procedureDescriptionId"
            v-bind="textFieldAttrs"
          />

          <Textfield
            :label="$t('procedureReport.fields.tussCode')"
            @input="setTussCode"
            @focus="focusTrackValues.tussCodeId = filter.tussCodeId"
            @blur="searchFromMainTextFilter(filter.tussCodeId, 'tussCodeId')"
            @click:clear="clearMainTextFilter('tussCodeId')"
            :value="filter.tussCodeId"
            v-bind="textFieldAttrs"
          />
        </div>
      </template>

      <template v-slot:expanded>
        <div class="procedure-report-filter__expanded">
          <PaginatedAutocompleteMulti
            :label="$t('procedureReport.fields.event')"
            :fetch="fetchEvents"
            @input="setEvent"
            :value="filter.eventId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <PaginatedAutocompleteMulti
            :key="`complement-${filter.eventId.join('-')}`"
            :label="$t('procedureReport.fields.complement')"
            :fetch="fetchComplements"
            :disabled="!filter.eventId.length"
            @input="setComplement"
            :value="filter.complementId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <PaginatedAutocompleteMulti
            :label="$t('procedureReport.fields.group')"
            :fetch="fetchGroups"
            @input="setGroup"
            :value="filter.groupId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <PaginatedAutocompleteMulti
            :key="`subgroup-${filter.groupId.join('-')}`"
            :label="$t('procedureReport.fields.subGroup')"
            :fetch="fetchSubGroups"
            :disabled="!filter.groupId.length"
            @input="setSubGroup"
            :value="filter.subGroupId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <PaginatedAutocompleteMulti
            :label="$t('procedureReport.fields.predictive')"
            :fetch="fetchPredictives"
            @input="setPredictive"
            :value="filter.predictiveId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <PaginatedAutocompleteMulti
            :key="`predictive-classification-${filter.predictiveId.join('-')}`"
            :label="$t('procedureReport.fields.predictiveClassification')"
            :fetch="fetchPredictiveClassifications"
            :disabled="!filter.predictiveId.length"
            @input="setPredictiveClassification"
            :value="filter.predictiveClassificationId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
          />

          <PaginatedAutocomplete
            :label="$t('procedureReport.fields.status')"
            :fetch="fetchStaticStatusOptions"
            :prepend-items="() => statusOptions"
            :value="selectedStatus"
            item-text="name"
            item-value="id"
            clearable
            :min-search-chars="0"
            :infinite="false"
            v-bind="autocompleteAttrs"
            @input="onStatusChange"
          />
        </div>
      </template>

      <template v-slot:actions="{ triggerSearch, triggerClear }">
        <div class="procedure-report-filter__actions">
          <v-btn
            outlined
            color="base-color"
            class="procedure-report-filter__action"
            @click="triggerClear">
            <v-icon left size="20">mdi-broom</v-icon>
            {{ $t('procedureReport.filter.clear') }}
          </v-btn>

          <v-btn
            color="primary"
            class="procedure-report-filter__action procedure-report-filter__action--search"
            @click="triggerSearch">
            <div class="mr-2" style="max-width: 18px;">
              <v-icon
                size="18"
                color="base-color">
                fas fa-search
              </v-icon>
            </div>
            {{ $t('common.search') }}
          </v-btn>
        </div>
      </template>
    </ExpandableFilterCard>
  </v-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ExpandableFilterCard from '@/components/Default/ExpandableFilterCard.vue';
import Textfield from '@/components/Default/Textfield.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import { procedureReportStatusConfig } from '@/store/modules/procedure-report/procedure-report-config';
import PaginatedAutocompleteMulti from '../Default/PaginatedAutocompleteMulti.vue';

export default {
  name: 'ProcedureReportFilter',
  components: { ExpandableFilterCard, PaginatedAutocomplete, PaginatedAutocompleteMulti, Textfield },
  data: () => ({
    focusTrackValues: {
      procedureCodeId: '',
      procedureDescriptionId: '',
      tussCodeId: '',
    },
  }),
  computed: {
    ...mapGetters({ filter: 'procedure-report/filter' }),
    selectedStatus() {
      const match = procedureReportStatusConfig.find((config) => this.filter.status.includes(config.id));
      return match?.id ?? null;
    },
    statusOptions() {
      return procedureReportStatusConfig.map((config) => ({
        id: config.id,
        name: this.$t(config.labelKey),
      }));
    },
    autocompleteAttrs() {
      return {
        clearable: true,
        regular: true,
        color: 'primary',
        itemColor: 'primary',
        placeholder: this.$t('procedureReport.filter.placeholders.select'),
        persistentPlaceholder: false,
        borderSelected: false,
        hideDetails: true,
        groupByHeader: false,
        minSearchChars: 0,
      };
    },
    textFieldAttrs() {
      return {
        clearable: true,
        regular: true,
        hideDetails: true,
        borderSelected: false,
        persistentPlaceholder: false,
        placeholder: this.$t('procedureReport.filter.placeholders.select'),
      };
    },
  },
  methods: {
    ...mapActions({
      resetStoreFilter: 'procedure-report/resetFilter',
      clearResults: 'procedure-report/clearResults',
      applyFilter: 'procedure-report/applyFilter',
      searchByFilter: 'procedure-report/searchByFilter',
      fetchClassification: 'procedure-report/fetchClassification',
      fetchCarrierOptions: 'procedure-report/fetchCarrierOptions',
      fetchEventOptions: 'procedure-report/fetchEventOptions',
      fetchGroupOptions: 'procedure-report/fetchGroupOptions',
      fetchSubGroupOptions: 'procedure-report/fetchSubGroupOptions',
      fetchComplementOptions: 'procedure-report/fetchComplementOptions',
      fetchPredictiveOptions: 'procedure-report/fetchPredictiveOptions',
      fetchPredictiveClassificationOptions: 'procedure-report/fetchPredictiveClassificationOptions',
    }),
    async fetchCarriers(page, size, inputValue) {
      return this.fetchCarrierOptions({ page, size, inputValue });
    },
    async fetchEvents(page, size, inputValue) {
      return this.fetchEventOptions({ page, size, inputValue });
    },
    async fetchGroups(page, size, inputValue) {
      return this.fetchGroupOptions({ page, size, inputValue });
    },
    async fetchSubGroups(page, size, inputValue) {
      return this.fetchSubGroupOptions({ page, size, inputValue, groupIds: this.filter.groupId });
    },
    async fetchComplements(page, size, inputValue) {
      return this.fetchComplementOptions({ page, size, inputValue, eventIds: this.filter.eventId });
    },
    async fetchPredictives(page, size, inputValue) {
      return this.fetchPredictiveOptions({ page, size, inputValue });
    },
    async fetchPredictiveClassifications(page, size, inputValue) {
      const payload = { page, size, inputValue, predictiveIds: this.filter.predictiveId };
      return this.fetchPredictiveClassificationOptions(payload);
    },
    normalizeFilterValues(values) {
      return Array.isArray(values) ? values : [];
    },
    async setCarrier(carrierId) {
      const normalizedCarrierIds = this.normalizeFilterValues(carrierId);
      this.applyFilter({ carrierId: normalizedCarrierIds });

      if (normalizedCarrierIds.length === 0) {
        this.clearResults();
        await this.fetchClassification();
        this.$nextTick(() => {
          this.$refs.ProcedureReportFilterForm.validate();
        });
        return;
      }

      this.$nextTick(() => {
        this.$refs.ProcedureReportFilterForm.resetValidation();
      });
      await this.searchByFilter();
    },
    setProcedureCode(procedureCodeId) {
      this.applyFilter({ procedureCodeId: procedureCodeId || '' });
    },
    setProcedureDescription(procedureDescriptionId) {
      this.applyFilter({ procedureDescriptionId: procedureDescriptionId || '' });
    },
    setTussCode(tussCodeId) {
      this.applyFilter({ tussCodeId: tussCodeId || '' });
    },
    setEvent(eventId) {
      this.applyFilter({
        eventId: this.normalizeFilterValues(eventId),
        complementId: [],
      });
    },
    setGroup(groupId) {
      this.applyFilter({
        groupId: this.normalizeFilterValues(groupId),
        subGroupId: [],
      });
    },
    setSubGroup(subGroupId) {
      this.applyFilter({ subGroupId: this.normalizeFilterValues(subGroupId) });
    },
    setComplement(complementId) {
      this.applyFilter({ complementId: this.normalizeFilterValues(complementId) });
    },
    setPredictive(predictiveId) {
      this.applyFilter({
        predictiveId: this.normalizeFilterValues(predictiveId),
        predictiveClassificationId: [],
      });
    },
    setPredictiveClassification(predictiveClassificationId) {
      this.applyFilter({ predictiveClassificationId: this.normalizeFilterValues(predictiveClassificationId) });
    },
    onStatusChange(value) {
      this.applyFilter({ status: value ? [value] : [] });
    },
    fetchStaticStatusOptions() {
      return Promise.resolve({ content: [], last: true });
    },
    async searchRecords() {
      this.$refs.ProcedureReportFilterForm.validate();

      await this.searchByFilter();
    },
    async searchFromMainTextFilter(value, field) {
      const previous = this.focusTrackValues[field];
      if (String(previous || '').trim() === String(value || '').trim()) return;
      await this.searchByFilter();
    },
    async clearMainTextFilter(field) {
      this.applyFilter({ [field]: '' });
      await this.searchByFilter();
    },
    async resetFilters() {
      await this.resetStoreFilter();
      await this.fetchClassification();
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/procedureReport/default.scss";
@import "@/shared/styles/procedureReport/filter.scss";
</style>

<style scoped>
.procedure-report-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot,
.procedure-report-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot {
  min-height: 42px;
  padding: 0 8px !important;
}

.procedure-report-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot > .v-select__slot,
.procedure-report-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot > .v-text-field__slot {
  align-items: center;
  min-height: 100%;
}

.procedure-report-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot > .v-select__slot > .v-label,
.procedure-report-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
  font-size: 12px;
  line-height: 20px;
}

.procedure-report-filter ::v-deep .autocomplete-default:not(.v-input--is-focused):not(.v-input--is-label-active) > .v-input__control > .v-input__slot > .v-select__slot > .v-label,
.procedure-report-filter ::v-deep .textfield-default:not(.v-input--is-focused):not(.v-input--is-label-active) > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
  top: 50% !important;
  transform: translateY(-50%);
}

.procedure-report-filter ::v-deep .autocomplete-default > .v-input__control > .v-input__slot > .v-select__slot input,
.procedure-report-filter ::v-deep .textfield-default > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  font-size: 13px;
  line-height: 20px;
}

.procedure-report-filter ::v-deep .textfield-default :is([class*="v-input__prepend"], [class*="v-input__append"]) {
  height: 42px !important;
}
</style>
